// Approval Modal Component - User approval for critical decisions
// frontend/src/components/ApprovalModal.tsx

import React, { useState, useEffect } from 'react';
import { agentManager } from '../services/agentManager';
import type { ApprovalRequest, Decision } from '../agents/specialized/ApprovalWorkflowAgent';

// Sub-component: Impact Analysis Panel
const ImpactAnalysisPanel: React.FC<{ decision: Decision }> = ({ decision }) => {
    const getCostClass = () => decision.cost_delta > 0 ? 'text-red-600' : decision.cost_delta < 0 ? 'text-green-600' : 'text-gray-600';
    const getRpnClass = () => decision.rpn_delta < 0 ? 'text-green-600' : decision.rpn_delta > 0 ? 'text-red-600' : 'text-gray-600';
    const getRiskClass = () => {
        switch (decision.risk_level) {
            case 'critical': case 'high': return 'bg-red-100 text-red-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-green-100 text-green-800';
        }
    };

    return (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
            <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-2">Impact Analysis</h4>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Cost Impact:</span>
                <span className={`font-bold text-lg ${getCostClass()}`}>
                    {decision.cost_delta > 0 ? '+' : ''}${decision.cost_delta.toFixed(2)}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">RPN Impact:</span>
                <span className={`font-bold text-lg ${getRpnClass()}`}>
                    {decision.rpn_delta > 0 ? '+' : ''}{decision.rpn_delta}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-gray-600">Risk Level:</span>
                <span className={`font-bold uppercase text-sm px-3 py-1 rounded-full ${getRiskClass()}`}>
                    {decision.risk_level}
                </span>
            </div>
            {decision.current_cost !== undefined && decision.budget !== undefined && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-gray-600">Current Budget:</span>
                    <span className="font-semibold">
                        ${decision.current_cost.toFixed(2)} / ${decision.budget.toFixed(2)}
                        <span className="text-xs text-gray-500 ml-2">
                            ({((decision.current_cost / decision.budget) * 100).toFixed(1)}%)
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
};

// Sub-component: Budget Warning
const BudgetWarning: React.FC<{ decision: Decision }> = ({ decision }) => {
    if (!decision.would_exceed_budget) return null;
    return (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-800 p-4 rounded mb-4">
            <div className="flex items-start">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                    <strong className="font-bold">Budget Violation</strong>
                    <p className="text-sm mt-1">
                        This decision would exceed the budget!
                        {decision.current_cost !== undefined && decision.budget !== undefined && (
                            <span className="block mt-1">
                                New total: ${(decision.current_cost + decision.cost_delta).toFixed(2)} &gt; ${decision.budget.toFixed(2)}
                            </span>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

// Sub-component: Decision Details
const DecisionDetailsPanel: React.FC<{ details: Decision['details'] }> = ({ details }) => {
    if (!details) return null;
    return (
        <div className="mb-4 space-y-2">
            {details.what_changes && (
                <div>
                    <h5 className="font-semibold text-gray-700 text-sm mb-1">What Changes:</h5>
                    <p className="text-gray-600 text-sm">{details.what_changes}</p>
                </div>
            )}
            {details.why_needed && (
                <div>
                    <h5 className="font-semibold text-gray-700 text-sm mb-1">Why Needed:</h5>
                    <p className="text-gray-600 text-sm">{details.why_needed}</p>
                </div>
            )}
            {details.alternatives && details.alternatives.length > 0 && (
                <div>
                    <h5 className="font-semibold text-gray-700 text-sm mb-1">Alternatives:</h5>
                    <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                        {details.alternatives.map((alt, idx) => (
                            <li key={idx}>{alt}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export const ApprovalModal: React.FC = () => {
    const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
    const [currentApproval, setCurrentApproval] = useState<ApprovalRequest | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!agentManager.isInitialized()) return;

        try {
            const approvalAgent = agentManager.getApprovalAgent();
            const pending = approvalAgent.getPendingApprovals();

            if (pending.length > 0 && !currentApproval && !isSubmitting) {
                setCurrentApproval(pending[0]);
            }

            setPendingApprovals(pending);
        } catch (error) {
            console.error('[ApprovalModal] Error checking approvals:', error);
        }
    }, [currentApproval, isSubmitting]);

    const handleChoice = async (choice: 'approve' | 'reject' | 'modify' | 'defer', reason?: string) => {
        if (!currentApproval || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const approvalAgent = agentManager.getApprovalAgent();
            await approvalAgent.userApprove(currentApproval.decision.id, choice, reason);
            console.log(`[ApprovalModal] Decision ${choice}: ${currentApproval.decision.title}`);
            setCurrentApproval(null);
        } catch (error) {
            console.error('[ApprovalModal] Error submitting approval:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!currentApproval) return null;

    const { decision } = currentApproval;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
                    <h2 className="text-2xl font-bold flex items-center">🔔 Approval Required</h2>
                    <p className="text-blue-100 text-sm mt-1">
                        {pendingApprovals.length} pending approval{pendingApprovals.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {/* Body */}
                <div className="p-6">
                    <div className="mb-4">
                        <h3 className="font-bold text-xl text-gray-900">{decision.title}</h3>
                        <p className="text-gray-600 mt-2 leading-relaxed">{decision.description}</p>
                    </div>

                    <ImpactAnalysisPanel decision={decision} />
                    <BudgetWarning decision={decision} />
                    <DecisionDetailsPanel details={decision.details} />
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex gap-2">
                    <button onClick={() => handleChoice('approve')} disabled={isSubmitting}
                        className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center">
                        ✅ Approve
                    </button>
                    <button onClick={() => handleChoice('reject', 'User rejected via modal')} disabled={isSubmitting}
                        className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center">
                        ❌ Reject
                    </button>
                    <button onClick={() => handleChoice('defer', 'User deferred decision')} disabled={isSubmitting}
                        className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center">
                        ⏸️ Defer
                    </button>
                </div>

                {/* Loading State */}
                {isSubmitting && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600">Processing...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

